// frontend/src/pages/HomePage.tsx

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ThemeToggle } from '../components/ThemeToggle';
import { LanguageSelector } from '../components/LanguageSelector';
import { TierBadge } from '../components/TierBadge';
import { Modal } from '../components/Modal';
import { Folders, ExternalLink, LogIn, LogOut, AlertCircle, RefreshCw, Search, Sparkles, ArrowRight, User, TrendingUp, Clock, Flame, Star, LayoutDashboard, Menu, X } from 'lucide-react';
import { MiniAppType, SubscriptionTier } from '../lib/supabase';
import { useProjects } from '../hooks/useApi';
import { invokeFunction } from '../lib/api';
import { Footer } from '../components/Footer';
import { useI18n } from '../contexts/I18nContext';
import { useStarredProjects } from '../hooks/useStarredProjects';
import { CreditBalanceBadge } from '../components/CreditBalanceBadge';
import { useTurnstile } from '../hooks/useTurnstile';
import { EMOJI_POOL } from '../data/emojiPool';

const DEFAULT_LOGO_EMOJI = '✨';

function pickRandomEmoji(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % EMOJI_POOL.length;
  return EMOJI_POOL[idx];
}

function resolveLogoEmoji(project: { id: string; logo_emoji: string | null }): string {
  const emoji = (project.logo_emoji ?? '').trim();
  if (!emoji || emoji === DEFAULT_LOGO_EMOJI) {
    return pickRandomEmoji(project.id);
  }
  return emoji;
}

interface PublicProject {
  id: string;
  name: string;
  description: string | null;
  type?: MiniAppType | null;
  slug: string | null;
  public_id: string | null;
  required_tier: SubscriptionTier | null;
  is_promoted: boolean;
  created_at: string;
  owner_is_admin: boolean;
  runs_total: number;
  runs_week: number;
  runs_month: number;
  published_at: string | null;
  logo_emoji: string | null;
}

interface SearchResult {
  project_id: string;
  name: string;
  description: string | null;
  slug: string | null;
  public_id: string | null;
  required_tier: string | null;
  type?: MiniAppType | null;
  similarity: number;
}

const PENDING_APP_QUERY_KEY = 'llambada_pending_app_query';

function calculatePublicMiniAppStats(allProjects: PublicProject[]) {
  const sevenDaysAgo = new Date(Date.now() - (7 * 24 * 60 * 60 * 1000));
  const userMiniAppsAddedThisWeek = allProjects.filter((project) => {
    const publishedAtRaw = project.published_at ?? project.created_at;
    const publishedAt = new Date(publishedAtRaw);
    if (Number.isNaN(publishedAt.getTime())) return false;
    return publishedAt >= sevenDaysAgo;
  }).length;

  return {
    totalPublicMiniApps: allProjects.length,
    userMiniAppsAddedThisWeek,
  };
}

export function HomePage() {
  const { user, session, signOut, ensureFreshToken } = useAuth();
  const { tt } = useI18n();
  const navigate = useNavigate();
  const { createProject } = useProjects();
  const { isStarred, toggleStar } = useStarredProjects();
  const {
    getToken: getTurnstileToken,
    TurnstileWidget,
    error: turnstileError,
    isEnabled: isTurnstileEnabled,
  } = useTurnstile();
  
  const [projects, setProjects] = useState<PublicProject[]>([]);
  const [publicMiniAppStats, setPublicMiniAppStats] = useState({
    totalPublicMiniApps: 0,
    userMiniAppsAddedThisWeek: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[] | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const searchResultsRef = useRef<HTMLDivElement | null>(null);
  const [activeCategory, setActiveCategory] = useState<'all' | MiniAppType>('all');
  const [sortMode, setSortMode] = useState<'newest' | 'top_month' | 'top_week'>('top_week');
  
  // AI Build flow state
  const [showBuildModal, setShowBuildModal] = useState(false);
  const [pendingAppTitle, setPendingAppTitle] = useState('');
  const [pendingAppQuery, setPendingAppQuery] = useState(''); // Original query for building
  const [generatingTitle, setGeneratingTitle] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const fetchPublicProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      const response = await fetch(
        `${supabaseUrl}/rest/v1/rpc/get_public_projects`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseAnonKey,
            'Authorization': `Bearer ${supabaseAnonKey}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const allPublicProjects = Array.isArray(data) ? data as PublicProject[] : [];
      setPublicMiniAppStats(calculatePublicMiniAppStats(allPublicProjects));
      
      // Filter mini-apps: show all admin mini-apps, only promoted non-admin mini-apps
      const filteredProjects = allPublicProjects.filter((project: PublicProject) => {
        return project.owner_is_admin || project.is_promoted === true;
      });

      setProjects(filteredProjects);
    } catch (err) {
      setError(err instanceof Error ? err.message : tt('Failed to load mini-apps'));
    } finally {
      setLoading(false);
    }
  }, [tt]);

  useEffect(() => {
    fetchPublicProjects();
  }, [fetchPublicProjects]);

  // Set page title and meta description
  useEffect(() => {
    document.title = tt('AI-Powered Mini-App Builder') + ' - Llambada';
    const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const original = meta?.content || '';
    if (meta) meta.content = 'Browse and use hundreds of free mini-apps built by creators with AI. Search by category, find tools, games, and utilities — or build your own without coding.';
    return () => { if (meta) meta.content = original; };
  }, [tt]);

  // Check for pending app query (for users returning after login)
  useEffect(() => {
    if (user) {
      const pendingQuery = localStorage.getItem(PENDING_APP_QUERY_KEY);
      if (pendingQuery) {
        // User is logged in and has a pending query - show the build modal
        generateTitleAndShowBuildModal(pendingQuery);
      }
    }
  }, [user]);

  const generateTitleAndShowBuildModal = async (query: string) => {
    setGeneratingTitle(true);
    setPendingAppQuery(query); // Store the original query for building
    try {
      const { data, error } = await invokeFunction<{ title: string }>(
        'generate-app-title',
        { query },
        { token: session?.access_token }
      );
      
      if (error || !data?.title) {
        // Fallback to simple title
        setPendingAppTitle(query.slice(0, 40));
      } else {
        setPendingAppTitle(data.title);
      }
      
      setShowBuildModal(true);
    } catch {
      setPendingAppTitle(query.slice(0, 40));
      setShowBuildModal(true);
    } finally {
      setGeneratingTitle(false);
    }
  };

  const getProjectUrl = (project: PublicProject | SearchResult) => {
    const publicId = project.public_id;
    return `/p/${publicId}${project.slug ? `/${project.slug}` : ''}`;
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    setSearchError(null);
    setHasSearched(true);
    
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      let authToken: string | null = null;
      if (user) {
        authToken = await ensureFreshToken({ forceRefresh: true });
      }

      const needsTurnstile = !authToken;
      const turnstileToken = needsTurnstile ? await getTurnstileToken() : null;

      if (needsTurnstile && isTurnstileEnabled && !turnstileToken) {
        setSearchError(turnstileError || tt('Verification could not be completed. Please try again.'));
        setSearchResults(null);
        return;
      }
      
      const response = await fetch(
        `${supabaseUrl}/functions/v1/search-miniapps`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseAnonKey,
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          },
          body: JSON.stringify({ query: searchQuery.trim(), limit: 3, turnstileToken }),
        }
      );

      if (!response.ok) {
        let message = tt('Search failed');
        try {
          const errorData = await response.json();
          if (typeof errorData?.error === 'string' && errorData.error.trim()) {
            message = errorData.error;
          }
        } catch {
          // Keep the generic translated message when the response is not JSON.
        }
        throw new Error(message);
      }

      const data = await response.json();
      setSearchResults(data.results || []);
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : tt('Search failed'));
      setSearchResults(null);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (!hasSearched || searching) return;

    window.requestAnimationFrame(() => {
      searchResultsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, [hasSearched, searching, searchResults, searchError]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleLetAIBuild = () => {
    // Show build confirmation modal for everyone.
    generateTitleAndShowBuildModal(searchQuery.trim());
  };

  const handleBuildApp = async () => {
    if (!pendingAppQuery) return;

    // If not authenticated yet, send user to sign in and restore flow afterward.
    if (!user || !session) {
      localStorage.setItem(PENDING_APP_QUERY_KEY, pendingAppQuery);
      setShowBuildModal(false);
      navigate('/login');
      return;
    }

    setCreatingProject(true);
    setCreateError(null);
    
    try {
      // Clear the pending query from localStorage
      localStorage.removeItem(PENDING_APP_QUERY_KEY);

      const titleToCreate = pendingAppTitle.trim() || pendingAppQuery.trim().slice(0, 40) || 'New mini-app';
      
      // Create a new mini-app
      const result = await createProject(titleToCreate, pendingAppQuery);
      
      if (result.error || !result.data) {
        setCreateError(result.error || tt('Failed to create mini-app'));
        return;
      }
      
      const projectId = result.data.id;
      
      // Create a conversation in the new mini-app
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const convResponse = await fetch(
        `${supabaseUrl}/rest/v1/conversations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${session.access_token}`,
            'Prefer': 'return=representation',
          },
          body: JSON.stringify({
            project_id: projectId,
            title: 'Building the app',
          }),
        }
      );
      
      if (!convResponse.ok) {
        throw new Error(tt('Failed to create conversation'));
      }
      
      const conversations = await convResponse.json();
      const conversation = Array.isArray(conversations) ? conversations[0] : conversations;
      
      if (!conversation?.id) {
        throw new Error(tt('Failed to create conversation'));
      }
      
      // Store the initial message to be sent when the conversation page loads
      const initialMessage = `Build a mini-app that does the following: ${pendingAppQuery}

Please create a complete, working mini-app with:
1. A user-friendly frontend interface
2. All necessary backend logic
3. Proper error handling
4. Clear instructions for the user`;
      
      sessionStorage.setItem(`pendingMessage_${conversation.id}`, initialMessage);
      
      // Navigate to the conversation page
      setShowBuildModal(false);
      setPendingAppQuery('');
      navigate(`/project/${projectId}/conversation/${conversation.id}`);
      
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : tt('Failed to create mini-app'));
    } finally {
      setCreatingProject(false);
    }
  };

  const handleCancelBuild = () => {
    localStorage.removeItem(PENDING_APP_QUERY_KEY);
    setShowBuildModal(false);
    setPendingAppTitle('');
    setPendingAppQuery('');
  };

  const visibleProjects = projects
    .filter((project) => {
      if (activeCategory === 'all') return true;
      return project.type === activeCategory;
    })
    .sort((a, b) => {
      if (sortMode === 'top_month') return b.runs_month - a.runs_month;
      if (sortMode === 'top_week') return b.runs_week - a.runs_week;
      return 0; // 'newest' keeps the original created_at DESC order from the API
    });

  const emptyStateTitle = (() => {
    if (projects.length === 0) return tt('No public mini-apps yet');
    if (activeCategory === 'game') return tt('No public games yet');
    if (activeCategory === 'utility') return tt('No public utilities yet');
    return tt('No public mini-apps yet');
  })();

  const emptyStateDescription = (() => {
    if (projects.length === 0) return tt('Check back later for new mini-apps.');
    if (activeCategory === 'game') return tt('Check back later for new game mini-apps.');
    if (activeCategory === 'utility') return tt('Check back later for new utility mini-apps.');
    return tt('Check back later for new mini-apps.');
  })();

  return (
    <div className="min-h-screen bg-theme-bg bg-mesh flex flex-col">
      <TurnstileWidget />
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-theme-border-muted bg-theme-bg/80 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 sm:h-16 items-center justify-between">
            <div className="flex items-center gap-2">
              <img
                src="/logo.png"
                alt="Llambada"
                className="h-5 w-5 rounded-md shadow-md shadow-accent-500/20"
              />
              <span className="font-display text-lg sm:text-xl font-semibold text-theme-text">
                Llambada
              </span>
            </div>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-2">
              {!user && <LanguageSelector />}
              {user ? (
                <>
                  <CreditBalanceBadge />
                  <Link to="/dashboard" className="btn btn-secondary text-sm">
                    <LayoutDashboard className="h-4 w-4" />
                    {tt('Dashboard')}
                  </Link>
                  <ThemeToggle />
                  <Link to="/profile" className="btn btn-ghost text-sm" title={tt('Profile & Settings')}>
                    <User className="h-4 w-4" />
                  </Link>
                  <button onClick={() => signOut()} className="btn btn-ghost text-sm" title={tt('Sign out')}>
                    <LogOut className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <>
                  <ThemeToggle />
                  <Link to="/login" className="btn btn-primary">
                    <LogIn className="h-4 w-4" />
                    {tt('Sign In')}
                  </Link>
                </>
              )}
            </div>

            {/* Mobile nav */}
            <div className="flex md:hidden items-center gap-2">
              {!user && <LanguageSelector />}
              {user && <CreditBalanceBadge />}
              <ThemeToggle />
              {user ? (
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="btn btn-ghost p-2"
                  aria-label={tt('Toggle menu')}
                >
                  {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              ) : (
                <Link to="/login" className="btn btn-primary text-sm px-3 py-2">
                  <LogIn className="h-4 w-4" />
                  <span className="hidden xs:inline">{tt('Sign In')}</span>
                </Link>
              )}
            </div>
          </div>

          {/* Mobile menu dropdown */}
          {showMobileMenu && user && (
            <div className="md:hidden border-t border-theme-border-muted py-3 space-y-1 animate-fade-in">
              <Link
                to="/dashboard"
                className="flex items-center gap-3 px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors"
                onClick={() => setShowMobileMenu(false)}
              >
                <LayoutDashboard className="h-4 w-4" />
                {tt('Dashboard')}
              </Link>
              <Link
                to="/profile"
                className="flex items-center gap-3 px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors"
                onClick={() => setShowMobileMenu(false)}
              >
                <User className="h-4 w-4" />
                {tt('Profile & Settings')}
              </Link>
              <button
                onClick={() => { signOut(); setShowMobileMenu(false); }}
                className="flex items-center gap-3 w-full px-3 py-3 text-sm text-theme-text hover:bg-theme-surface-hover rounded-lg transition-colors text-left"
              >
                <LogOut className="h-4 w-4" />
                {tt('Sign out')}
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
          {/* Hero Section */}
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="font-display text-2xl sm:text-4xl md:text-5xl font-bold text-theme-text mb-2 sm:mb-4">
              {tt('Public Mini-Apps')}
            </h1>
            <p className="text-base sm:text-xl text-theme-text-secondary max-w-2xl mx-auto">
              {tt('Explore and use mini-apps created by our community')}
            </p>
            {!loading && !error && (
              <p className="text-sm sm:text-base text-theme-text-muted max-w-3xl mx-auto mt-3">
                {tt(
                  'Currently on the platform: {total} mini-apps. Added by the users this week: {weekly} mini-apps.',
                  {
                    total: publicMiniAppStats.totalPublicMiniApps,
                    weekly: publicMiniAppStats.userMiniAppsAddedThisWeek,
                  }
                )}
              </p>
            )}
          </div>

          {/* Category Filters & Sort */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
            <button
              type="button"
              onClick={() => setActiveCategory('all')}
              className={`btn text-sm sm:text-base px-3 sm:px-4 py-2 ${activeCategory === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            >
              {tt('All mini-apps')}
            </button>
            <button
              type="button"
              onClick={() => setActiveCategory('game')}
              className={`btn text-sm sm:text-base px-3 sm:px-4 py-2 ${activeCategory === 'game' ? 'btn-primary' : 'btn-secondary'}`}
            >
              {tt('Games')}
            </button>
            <button
              type="button"
              onClick={() => setActiveCategory('utility')}
              className={`btn text-sm sm:text-base px-3 sm:px-4 py-2 ${activeCategory === 'utility' ? 'btn-primary' : 'btn-secondary'}`}
            >
              {tt('Utilities')}
            </button>
          </div>

          {/* Sort Tabs */}
          <div className="flex flex-wrap items-center justify-center gap-1 mb-6 sm:mb-8">
            <button
              type="button"
              onClick={() => setSortMode('newest')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                sortMode === 'newest'
                  ? 'bg-theme-surface text-theme-text border border-theme-border'
                  : 'text-theme-text-muted hover:text-theme-text'
              }`}
            >
              <Clock className="h-3.5 w-3.5" />
              {tt('Newest')}
            </button>
            <button
              type="button"
              onClick={() => setSortMode('top_month')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                sortMode === 'top_month'
                  ? 'bg-theme-surface text-theme-text border border-theme-border'
                  : 'text-theme-text-muted hover:text-theme-text'
              }`}
            >
              <TrendingUp className="h-3.5 w-3.5" />
              {tt('Top this month')}
            </button>
            <button
              type="button"
              onClick={() => setSortMode('top_week')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                sortMode === 'top_week'
                  ? 'bg-theme-surface text-theme-text border border-theme-border'
                  : 'text-theme-text-muted hover:text-theme-text'
              }`}
            >
              <Flame className="h-3.5 w-3.5" />
              {tt('Top this week')}
            </button>
          </div>

          {/* Search Section */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <textarea
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={tt('Type the app you are looking for, for example: "Fill and sign a PDF form"')}
                rows={2}
                className="input w-full pr-12 resize-none text-base"
                disabled={searching}
              />
              <button
                onClick={handleSearch}
                disabled={searching || !searchQuery.trim()}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-accent-500 text-white hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title={tt('Search')}
              >
                {searching ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <Search className="h-5 w-5" />
                )}
              </button>
            </div>
            <p className="text-xs text-theme-text-muted text-center mt-2">
              {tt('Press Enter to search')}
            </p>
          </div>

          {/* Search Results */}
          {hasSearched && !searching && (
            <div ref={searchResultsRef} className="max-w-4xl mx-auto mb-12 scroll-mt-24">
              {searchError ? (
                <div className="text-center py-6">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-theme-error-bg mb-4">
                    <AlertCircle className="h-6 w-6 text-theme-error" />
                  </div>
                  <p className="text-theme-text-secondary">{searchError}</p>
                </div>
              ) : (searchResults || []).length === 0 ? (
                <div className="text-center py-6">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-theme-surface mb-4">
                    <Search className="h-6 w-6 text-theme-text-muted" />
                  </div>
                  <p className="text-theme-text-secondary mb-4">{tt('No mini-apps found matching your search')}</p>
                </div>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-theme-text mb-4 text-center">
                    {tt('Top Results')}
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6">
                    {(searchResults || []).map((result) => (
                      <Link
                        key={result.project_id}
                        to={getProjectUrl(result)}
                        className="card p-5 hover:border-accent-500/50 transition-all duration-200 group"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-accent-500/20 to-cyan-500/20 group-hover:from-accent-500/30 group-hover:to-cyan-500/30 transition-colors">
                            <Folders className="h-5 w-5 text-accent-500" />
                          </div>
                          <div className="flex items-center gap-2">
                            {result.required_tier && (
                              <TierBadge tier={result.required_tier as SubscriptionTier} size="sm" />
                            )}
                            <ExternalLink className="h-4 w-4 text-theme-text-muted group-hover:text-accent-500 transition-colors" />
                          </div>
                        </div>
                        <h4 className="font-semibold text-theme-text mb-1 group-hover:text-accent-500 transition-colors line-clamp-1">
                          {result.name}
                        </h4>
                        {result.description && (
                          <p className="text-theme-text-secondary text-sm line-clamp-2">
                            {result.description}
                          </p>
                        )}
                      </Link>
                    ))}
                  </div>
                </>
              )}
              
              {/* AI Build CTA */}
              <div className="text-center py-4 border-t border-theme-border-muted">
                <p className="text-theme-text-secondary mb-2">
                  {tt("Don't see a mini-app that you are looking for?")}
                </p>
                <button
                  onClick={handleLetAIBuild}
                  className="inline-flex items-center gap-2 text-accent-500 hover:text-accent-400 font-medium transition-colors"
                >
                  <Sparkles className="h-5 w-5" />
                  {tt('Let our AI build this app for you')}
                  <ArrowRight className="h-4 w-4" />
                </button>
                <p className="text-sm text-theme-text-muted mt-1">
                  {tt('and make $$ when other users use it')}
                </p>
              </div>
            </div>
          )}

          {/* Mini-Apps Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-theme-error-bg mb-6">
                <AlertCircle className="h-8 w-8 text-theme-error" />
              </div>
              <h3 className="font-display text-xl font-semibold text-theme-text mb-2">
                {tt('Failed to load mini-apps')}
              </h3>
              <p className="text-theme-text-secondary mb-6">
                {error}
              </p>
              <button
                onClick={() => fetchPublicProjects()}
                className="btn btn-primary"
              >
                <RefreshCw className="h-4 w-4" />
                {tt('Try Again')}
              </button>
            </div>
          ) : visibleProjects.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-theme-surface mb-6">
                <Folders className="h-8 w-8 text-theme-text-muted" />
              </div>
              <h3 className="font-display text-xl font-semibold text-theme-text mb-2">
                {emptyStateTitle}
              </h3>
              <p className="text-theme-text-secondary mb-6">
                {emptyStateDescription}
              </p>
              <button
                onClick={() => fetchPublicProjects()}
                className="btn btn-secondary"
              >
                <RefreshCw className="h-4 w-4" />
                {tt('Refresh')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {visibleProjects.map((project) => (
                <Link
                  key={project.id}
                  to={getProjectUrl(project)}
                  className="card p-6 hover:border-accent-500/50 transition-all duration-200 group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-accent-500/20 to-cyan-500/20 group-hover:from-accent-500/30 group-hover:to-cyan-500/30 transition-colors text-2xl leading-none">
                      <span aria-hidden="true">{resolveLogoEmoji(project)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {project.required_tier && (
                        <TierBadge tier={project.required_tier} size="sm" />
                      )}
                      {user && (
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleStar(project.id); }}
                          className="p-0.5 rounded transition-colors hover:bg-theme-surface-hover"
                          title={isStarred(project.id) ? tt('Unstar') : tt('Star')}
                        >
                          <Star
                            className={`h-4 w-4 transition-colors ${
                              isStarred(project.id)
                                ? 'text-amber-500'
                                : 'text-theme-text-muted opacity-50 group-hover:text-theme-text-muted group-hover:opacity-70'
                            }`}
                            fill={isStarred(project.id) ? 'currentColor' : 'none'}
                          />
                        </button>
                      )}
                      <ExternalLink className="h-5 w-5 text-theme-text-muted group-hover:text-accent-500 transition-colors" />
                    </div>
                  </div>
                  <h3 className="font-display text-lg font-semibold text-theme-text mb-2 group-hover:text-accent-500 transition-colors">
                    {project.name}
                  </h3>
                  {project.description && (
                    <p className="text-theme-text-secondary text-sm line-clamp-2">
                      {project.description}
                    </p>
                  )}
                </Link>
              ))}
            </div>
          )}

          {/* Become a Creator CTA */}
          <div className="mt-12 text-center border-t border-theme-border-muted pt-8">
            <p className="text-theme-text-secondary mb-3">
              {tt('Want to build mini-apps like these?')}
            </p>
            <Link
              to="/creators"
              className="inline-flex items-center gap-2 text-accent-500 hover:text-accent-400 font-medium transition-colors"
            >
              <Sparkles className="h-5 w-5" />
              {tt('Become a Creator')}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </main>

      <Footer variant="full" />

      {/* Build App Confirmation Modal */}
      <Modal
        isOpen={showBuildModal}
        onClose={handleCancelBuild}
        title={tt('Build Your Mini-App')}
        size="sm"
      >
        <div className="text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-accent-500/20 to-cyan-500/20 mb-4">
            <Sparkles className="h-7 w-7 text-accent-500" />
          </div>
          {generatingTitle ? (
            <div className="flex items-center justify-center gap-2 mb-4">
              <LoadingSpinner size="sm" />
              <span className="text-theme-text-secondary">{tt('Generating title...')}</span>
            </div>
          ) : (
            <>
              <p className="text-theme-text-secondary mb-2">
                {tt('Ready to build your new mini-app?')}
              </p>
              <div className="mb-4 text-left">
                <label htmlFor="pendingAppTitle" className="block text-sm font-medium text-theme-text-secondary mb-2">
                  {tt('Mini-app name')}
                </label>
                <input
                  id="pendingAppTitle"
                  type="text"
                  value={pendingAppTitle}
                  onChange={(e) => setPendingAppTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (
                      e.key === 'Enter' &&
                      !creatingProject &&
                      !generatingTitle &&
                      pendingAppTitle.trim()
                    ) {
                      e.preventDefault();
                      handleBuildApp();
                    }
                  }}
                  className="input w-full"
                  autoFocus
                />
              </div>
              <p className="text-sm text-theme-text-muted mb-6">
                {tt("Explain verbally what your mini-app will have to do and our AI will build it for you. You'll be able to test, refine, and publish it.")}
              </p>
            </>
          )}
          
          {createError && (
            <div className="p-3 rounded-lg bg-theme-error-bg border border-theme-error-border text-sm text-theme-error mb-4">
              {createError}
            </div>
          )}
          
          <div className="flex gap-3">
            <button
              onClick={handleCancelBuild}
              disabled={creatingProject}
              className="btn btn-secondary flex-1"
            >
              {tt('Cancel')}
            </button>
            <button
              onClick={handleBuildApp}
              disabled={creatingProject || generatingTitle || !pendingAppTitle.trim()}
              className="btn btn-primary flex-1"
            >
              {creatingProject ? (
                <>
                  <LoadingSpinner size="sm" />
                  {tt('Creating...')}
                </>
              ) : (
                tt('Yes, build my mini-app')
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
